import { type ReactNode, useState, useRef, useEffect, useCallback } from "react"
import { useWindowSize } from "@react-hookz/web"
import { motion, useAnimation, AnimatePresence } from "framer-motion"
import { createPortal } from "react-dom"
import cc from "classnames"
import { disableBodyScroll, enableBodyScroll, clearAllBodyScrollLocks } from "scroll"

import { MODAL_ROOT_ID } from "src/constants"
import useDialog from "@hooks/useDialog"
import useSubsequentEffect from "@hooks/useSubsequentEffect"

interface BottomSheetProps {
  is_open: boolean
  onClose?: () => void
  children: ReactNode
  title?: string
  prevent_closing?: boolean
  with_padding?: boolean
  disable_body_scrolling?: boolean
  id: string
}

const BottomSheet = (props: BottomSheetProps): JSX.Element | null => {
  const [isMounted, setIsMounted] = useState(false)
  const { width } = useWindowSize()
  const isMobile = width <= 768

  const { is_open, prevent_closing, onClose } = props
  const dialogRef = useRef(null)
  const controls = useAnimation()

  useEffect(() => {
    setIsMounted(true)
  }, [])

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key == "Escape" && onClose) {
        onClose()
      }
    },
    [onClose]
  )

  useEffect(() => {
    document.addEventListener("keydown", handleKeyDown)
    return () => {
      document.removeEventListener("keydown", handleKeyDown)
    }
  }, [handleKeyDown])

  useSubsequentEffect(() => {
    if (is_open) {
      if (props.disable_body_scrolling) {
        disableBodyScroll(dialogRef.current)
      }
      controls.start("visible")
    } else {
      if (props.disable_body_scrolling) {
        // TODO: if body scroll was already being locked by the happy-cart component, this reference
        // gets lost and this doesn't fire
        enableBodyScroll(dialogRef.current)
      }
      controls.start("hidden")
    }
  }, [is_open]) // eslint-disable-line

  useEffect(() => {
    if (props.disable_body_scrolling) {
      clearAllBodyScrollLocks()
      return clearAllBodyScrollLocks
    }
  }, []) // eslint-disable-line

  const attributes = useDialog({
    id: props.id,
    isOpen: is_open,
  })

  // We only use this component on the client
  if (!isMounted) {
    return null
  }

  return createPortal(
    <div className="fixed z-[100000]">
      <AnimatePresence initial={false}>
        {is_open && (
          <motion.div
            key={1}
            className="fixed top-0 left-0 w-full h-full z-[60]"
            ref={dialogRef}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
          >
            <div className="fixed top-0 left-0 w-full h-full bg-black opacity-60 md:opacity-30" />
            {/* cover for behind the bottom sheet for ios overflow scrolling */}
            <div className="fixed bottom-0 left-0 w-full h-96 bg-white md:hidden" />
          </motion.div>
        )}
        {is_open && (
          <motion.div
            key={2}
            {...attributes.container}
            className="fixed top-0 left-0 w-full h-full overflow-auto z-[70] md:flex md:items-center md:justify-center"
            /* passing { opacity: 1 } and { y: 0 } ensures we can smoothly switch between desktop and mobile */
            initial={isMobile ? { y: "100%", opacity: 1 } : { opacity: 0, y: 0 }}
            animate={isMobile ? { y: 0, opacity: 1 } : { opacity: 1, y: 0 }}
            exit={isMobile ? { y: "100%", opacity: 1 } : { opacity: 0, y: 0 }}
            transition={{ duration: 0.3 }}
          >
            <div
              {...attributes.overlay}
              className="fixed top-0 left-0 w-full h-screen"
              onClick={prevent_closing ? undefined : onClose}
            />
            <div className="min-h-screen flex justify-end flex-col md:justify-center">
              <div
                {...attributes.dialog}
                className={cc(
                  // transform-gpu is a hack to fix a rendering issue in chrome
                  // (the top left border radius is disabled during the transition)
                  "relative rounded-t-[1.6rem] md:bottom-auto md:rounded-[3.2rem] overflow-hidden bg-white transform-gpu",
                  {
                    "p-24 pt-40 md:p-32 md:pt-40": props.with_padding,
                  }
                )}
              >
                {props.title && (
                  <p {...attributes.title} className="sr-only">
                    {props.title}
                  </p>
                )}
                {props.children}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>,
    document.querySelector(`#${MODAL_ROOT_ID}`) as Element
  )
}

BottomSheet.defaultProps = {
  disableBodyScrolling: true,
}

export default BottomSheet
